import { useCallback, useState } from 'react'
import store from 'store'

/**
 * Rough idea adopted from https://usehooks.com/useLocalStorage/ and modified
 * for our own needs.
 *
 * Usage: as with `useState`, but persists to local storage
 */
export const useLocalStorage = <T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const storedInitialValue = store.get(key)
      if (storedInitialValue === undefined) {
        store.set(key, initialValue)
        return initialValue
      } else {
        return storedInitialValue
      }
    } catch (error) {
      console.error(
        `Couldn't retrieve initialValue from localStore for "${key}"`,
        error,
      )
      return initialValue
    }
  })

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        // Compute valueToStore outside of setStoredValue to avoid
        // side effects inside the state updater (required for React 18+ concurrent mode)
        const valueToStore =
          value instanceof Function ? value(storedValue) : value
        setStoredValue(valueToStore)
        store.set(key, valueToStore)
      } catch (error) {
        console.error(`Couldn't save value to localStore for "${key}"`, error)
        throw error
      }
    },
    [key, storedValue],
  )

  return [storedValue, setValue]
}
